import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { observer, inject } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { Alert } from 'reactstrap';
import { get } from 'lodash';

import { modelOf } from '../../../prop-types';
import Product from '../../../models/Product';
import AddToCart from '../../cart/AddToCart';
import Analytics from '../../../analytics/Analytics';
import CartStore from '../../../store/CartStore';
import ProductClass from '../../../types/ProductClass';
import ProductAvailabilityType from '../../../types/ProductAvailabilityType';
import AccountStore from '../../../store/AccountStore';
import ConfigStore from '../../../store/ConfigStore';
import ProductSeasonButton from '../ProductSeasonButton';
import ProductWatcherWidget from '../../product-watcher/ProductWatcherWidget';
import RequestState from '../../../types/RequestState';
import ProductReserveInStoreButton from '../ProductReserveInStoreButton';
import globalTranslations from '../../../i18n/globalTranslations';
import SimpleAddToCart from '../../cart/SimpleAddToCart';
import CartIcon from '../../cart/CartIcon';
import { calculateNearestProductQuantityWithQuantityStep } from '../../../util/number';
import UnexpectedError from '../../loader/UnexpectedError';
import ProductRecurringOrder from '../ProductRecurringOrder';
import RecurringOrderStore from '../../../store/RecurringOrderStore';
import UIStore from '../../../store/UIStore';

export const DEFAULT_MAX_QUANTITY = 99999;

@observer
class ProductAddToCart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      addToCartError: null,
      messageOK: null
    };

    // Saving the function as a reference so we don't create a new Math-function on every render.
    this.nearestProductQuantity = calculateNearestProductQuantityWithQuantityStep;
    this.mathFloor = Math.floor;
    this.mathMin = Math.min;
  }

  getMaxByPackage = (product) => {
    const { activeProductId } = this.props;
    return product.sellInPackage
      ? this.mathFloor(
          product.getFreeQuantity(activeProductId) / product.package_size
        ) * product.package_size
      : product.getFreeQuantity(activeProductId);
  };

  getMaxQuantity = (product) => {
    const { accountStore, configStore } = this.props;

    const backOrderLimit =
      configStore.product.backorderLimit > 0
        ? configStore.product.backorderLimit
        : DEFAULT_MAX_QUANTITY;

    const normalMaxLimit = product.canBeOrderedOutOfStock
      ? DEFAULT_MAX_QUANTITY
      : this.getMaxByPackage(product);

    const maxAccountQuantity =
      accountStore.account && accountStore.account.max_product_quantity > 0
        ? accountStore.account.max_product_quantity
        : DEFAULT_MAX_QUANTITY;
    const maxBackorderQuantity =
      product.availability_type === ProductAvailabilityType.ALLOW_BACKORDER
        ? backOrderLimit
        : DEFAULT_MAX_QUANTITY;
    const maxNormalProductQuantity =
      product.availability_type !== ProductAvailabilityType.ALLOW_BACKORDER
        ? normalMaxLimit
        : DEFAULT_MAX_QUANTITY;

    return this.mathMin(
      maxAccountQuantity,
      maxBackorderQuantity,
      maxNormalProductQuantity,
      DEFAULT_MAX_QUANTITY
    );
  };

  handleAddToCartClick = (quantity, services = [], newRecurring = {}) => {
    const {
      cartStore,
      analytics,
      product,
      activeProductId,
      accountStore,
      addToCartProduct,
      productListView
    } = this.props;

    // If cartStore is already loading, don't do anything
    if (cartStore.state === RequestState.LOADING) {
      return;
    }
    if (activeProductId) {
      const withTax = accountStore.showPricesWithTax;
      const quantityStep = addToCartProduct.getQuantityStep(product);
      const newQuantity = this.nearestProductQuantity(quantity, quantityStep);

      const withActiveCategory = productListView !== true;

      this.setState({ loading: true });
      cartStore
        .addToCart(
          activeProductId,
          newQuantity,
          [],
          services,
          newRecurring,
          withActiveCategory
        )
        .then(() => {
          this.setState({
            loading: false,
            addToCartError: null,
            messageOK: null
          });
          analytics.addToCart([
            {
              product,
              newQuantity,
              activeProductId,
              price: product.getPrice(withTax, activeProductId)
            }
          ]);
          analytics.cartStatus({
            cart: cartStore.cart
          });
        })
        .catch((e) => {
          this.setState({
            loading: false,
            addToCartError: e,
            messageOK: null
          });
        });
    }
  };

  handleAddToOrderClick = (orderId, quantity, cycle) => {
    const {
      cartStore,
      recurringOrderStore,
      analytics,
      product,
      activeProductId,
      accountStore,
      addToCartProduct
    } = this.props;

    // If cartStore is already loading, don't do anything
    if (recurringOrderStore.state === RequestState.LOADING) {
      return;
    }
    if (activeProductId) {
      const withTax = accountStore.showPricesWithTax;
      const quantityStep = addToCartProduct.getQuantityStep(product);
      const newQuantity = this.nearestProductQuantity(quantity, quantityStep);

      this.setState({ loading: true });

      const orderProduct = Object.assign(
        {
          quantity: newQuantity,
          extendedId: activeProductId
        },
        cycle
      );

      recurringOrderStore
        .addProduct(orderId, orderProduct)
        .then(() => {
          this.setState({
            loading: false,
            messageOK: true
          });
          analytics.addToCart([
            {
              product,
              newQuantity,
              activeProductId,
              price: product.getPrice(withTax, activeProductId)
            }
          ]);
          analytics.cartStatus({
            cart: cartStore.cart
          });
        })
        .catch((e) => {
          this.setState({
            loading: false,
            messageOK: false
          });
        });
    }
  };

  handleRecurringToCart = (newRecurring) => {
    this.handleAddToCartClick(this.props.quantity, [], newRecurring);
  };

  handleRecurringToOrder = (orderId, newRecurring) => {
    this.handleAddToOrderClick(orderId, this.props.quantity, newRecurring);
  };

  updateQuantity = (quantity) => {
    if (this.props.onUpdateProductQuantity) {
      this.props.onUpdateProductQuantity(quantity);
    }
  };

  renderMinOrderQuantityErrorBanner = () => {
    const { quantity, addToCartProduct } = this.props;
    if (
      !addToCartProduct.min_order_quantity ||
      quantity >= addToCartProduct.min_order_quantity
    ) {
      return null;
    }

    return (
      <Alert
        color="light"
        className="ProductAddToCart__minimum-order-quantity-banner"
      >
        <FormattedMessage
          id="product.minOrderQuantityError"
          defaultMessage="Products minimum quantity is {minOrderQuantity} {stockUnit}"
          values={{
            minOrderQuantity: addToCartProduct.min_order_quantity,
            stockUnit: addToCartProduct.stock_unit
          }}
        />
      </Alert>
    );
  };

  renderError = () => {
    const { addToCartError } = this.state;

    if (!addToCartError) {
      return null;
    }

    let errors = get(addToCartError, 'response.data.errors');

    if (!errors) {
      errors = get(addToCartError, 'response.data');
    }

    if (errors) {
      return (
        <Alert className="ProductAddToCart__errors" color="danger">
          {Object.values(errors).map((message) => (
            <div key={message}>{message}</div>
          ))}
        </Alert>
      );
    }

    return <UnexpectedError />;
  };

  renderOrderMessage = () => {
    const { messageOK } = this.state;

    return messageOK ? (
      <Alert className="ProductAddToCart__errors" color="success">
        <FormattedMessage
          id="order.productAddedSentence"
          defaultMessage="Product added to order"
        />
      </Alert>
    ) : (
      <Alert className="ProductAddToCart__errors" color="danger">
        <FormattedMessage
          id="order.productAddFailedSentence"
          defaultMessage="Product cannot be added for order"
        />
      </Alert>
    );
  };

  render() {
    const {
      cartStore,
      accountStore,
      configStore,
      uiStore,
      product,
      activeProductId,
      addToCartProduct,
      simple,
      withReserveInStore,
      withRecurringOrder,
      withServices,
      hideQuantity,
      quantity,
      ifWebStore,
      withQuantityPicker,
      isValid,
      scrollToServices,
      ...rest
    } = this.props;

    const { addToCartError, messageOK, loading } = this.state;

    if (product.season && !product.season.hasStarted()) {
      return <ProductSeasonButton season={product.season} />;
    }

    const ifShoppingCenter = configStore.siteConfig.isShoppingCenter;
    const ifBuyingAllowed =
      product.merchantInfo && product.merchantInfo.buyingAllowed;

    if (ifShoppingCenter && !ifBuyingAllowed) {
      return null;
    }

    if (activeProductId && product.isTemporarilyUnavailable(activeProductId)) {
      let productNameWithModel =
        addToCartProduct.name + ' ' + addToCartProduct.model;
      if (product.class === ProductClass.COLLECTION) {
        const collectionItem = product.collection.getItemWithProductId(
          activeProductId
        );
        if (collectionItem && collectionItem.name) {
          productNameWithModel =
            productNameWithModel + ' – ' + collectionItem.name;
        }
      }
      return !simple ? (
        <ProductWatcherWidget
          productName={productNameWithModel}
          activeProductId={activeProductId}
        />
      ) : null;
    }

    const onlyInShop =
      addToCartProduct.availability_type ===
      ProductAvailabilityType.ONLY_IN_SHOP;

    const soldOut =
      activeProductId &&
      addToCartProduct.availability_type === ProductAvailabilityType.CLOSEOUT &&
      !product.hasEnoughStockAvailable(activeProductId);

    const hideAddToCart =
      onlyInShop ||
      (product.season && product.season.hasEnded()) ||
      (soldOut &&
        // Don't hide for collection or multi products to avoid confusion caused
        // by arriving to the page and not seeing an add to cart button.
        product.class !== ProductClass.COLLECTION &&
        product.class !== ProductClass.MULTI);

    if (hideAddToCart) {
      return null;
    }

    const disabled = !activeProductId || soldOut || !isValid;

    let disabledMessage;
    if (!activeProductId) {
      disabledMessage = (
        <FormattedMessage
          id="product.selectProductFirst"
          defaultMessage="First choose the product."
        />
      );
    } else if (
      (product.class !== ProductClass.COLLECTION ||
        product.class !== ProductClass.MULTI) &&
      soldOut
    ) {
      disabledMessage = (
        <FormattedMessage
          id="product.currentProductSoldOut"
          defaultMessage="The selected product has been sold out."
        />
      );
    } else if (soldOut) {
      disabledMessage = (
        <FormattedMessage
          id="product.hasBeenSoldOut"
          defaultMessage="This product has been sold out."
        />
      );
    } else if (!isValid) {
      disabledMessage = (
        <FormattedMessage
          id="product.checkingShippingValidity"
          defaultMessage="Product validity for the cart."
        />
      );
    }

    let tooltipMessage;
    const updateInProgress = cartStore.state === RequestState.LOADING;
    if (updateInProgress) {
      tooltipMessage = (
        <FormattedMessage
          id="cart.beingUpdated"
          defaultMessage="Cart is being updated"
        />
      );
    }

    let quantityAllowed =
      accountStore.isRetailer || configStore.productCard.showQuantitySelection;
    if (hideQuantity) {
      quantityAllowed = false;
    }

    let content = null;

    const quantityStep = addToCartProduct.getQuantityStep(product);
    const withTax = accountStore.showPricesWithTax;
    const buyingAllowed = !(
      configStore.recurringOrder.enabled &&
      accountStore.loggedIn &&
      addToCartProduct.hasOnlyRecurringOrder()
    );

    if (!simple) {
      content = (
        <Fragment>
          <div className="ProductAddToCart ProductAddToCart--normal">
            {buyingAllowed && (
              <AddToCart
                key={addToCartProduct.id}
                notAllowed={updateInProgress}
                disabled={disabled}
                tooltipMessage={disabledMessage || tooltipMessage}
                onAddToCartClick={this.handleAddToCartClick}
                maxQuantity={this.getMaxQuantity(addToCartProduct)}
                quantityStep={quantityStep}
                unit={addToCartProduct.stock_unit}
                loading={loading}
                withServices={withServices}
                withTax={withTax}
                product={product}
                onUpdateProductQuantity={this.updateQuantity}
                quantity={quantity}
                minOrderQuantity={addToCartProduct.min_order_quantity}
                ifWebStore={ifWebStore}
                withQuantityPicker={withQuantityPicker}
                scrollToServices={scrollToServices}
                {...rest}
              />
            )}
            {this.renderMinOrderQuantityErrorBanner()}
            {addToCartError && this.renderError()}
          </div>
          {withRecurringOrder && (
            <ProductRecurringOrder
              product={product}
              withTax={withTax}
              onAddToCartClick={this.handleRecurringToCart}
              onAddToOrderClick={this.handleRecurringToOrder}
              disabled={disabled}
              loading={loading}
            />
          )}
          {withRecurringOrder &&
            messageOK !== null &&
            this.renderOrderMessage()}
          {withReserveInStore &&
            !product.isBlockedFromStoreReservation(
              configStore.reserveInStore.excludePropertyId
            ) && (
              <div className="ProductReserveInStore__wrapper">
                <FormattedMessage {...globalTranslations.buttonInfo} />
                <ProductReserveInStoreButton
                  product={product}
                  activeProductId={activeProductId}
                />
              </div>
            )}
        </Fragment>
      );
    } else if (simple && !disabled && buyingAllowed) {
      content = quantityAllowed ? (
        <SimpleAddToCart
          notAllowed={updateInProgress}
          loading={loading}
          maxQuantity={this.getMaxQuantity(addToCartProduct)}
          quantityStep={quantityStep}
          onAddToCartClick={this.handleAddToCartClick}
          unit={addToCartProduct.stock_unit}
          tooltipMessage={disabledMessage || tooltipMessage}
          disabled={disabled}
          onUpdateProductQuantity={this.updateQuantity}
          minOrderQuantity={addToCartProduct.min_order_quantity}
          quantity={quantity}
          ifWebStore={ifWebStore}
        />
      ) : (
        ifWebStore && (
          <CartIcon
            notAllowed={updateInProgress}
            onClick={() => this.handleAddToCartClick(1)}
            loading={loading}
            tooltipMessage={tooltipMessage}
          />
        )
      );
    }

    return content;
  }
}

ProductAddToCart.propTypes = {
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  accountStore: modelOf(AccountStore).isRequired,
  cartStore: modelOf(CartStore).isRequired,
  recurringOrderStore: modelOf(RecurringOrderStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  product: modelOf(Product).isRequired,
  addToCartProduct: PropTypes.object.isRequired,
  onUpdateProductQuantity: PropTypes.func,
  scrollToServices: PropTypes.func,
  activeProductId: PropTypes.string,
  simple: PropTypes.bool,
  withReserveInStore: PropTypes.bool,
  withRecurringOrder: PropTypes.bool,
  withServices: PropTypes.bool,
  withQuantityPicker: PropTypes.bool,
  hideQuantity: PropTypes.bool,
  price: PropTypes.node,
  history: PropTypes.object,
  columnId: PropTypes.number,
  rowId: PropTypes.number,
  quantity: PropTypes.number,
  ifWebStore: PropTypes.bool,
  isValid: PropTypes.bool
};

ProductAddToCart.defaultProps = {
  simple: false,
  hideQuantity: false,
  ifWebStore: true,
  withQuantityPicker: true,
  isValid: true
};

export default inject(
  'analytics',
  'accountStore',
  'cartStore',
  'configStore',
  'productStore',
  'uiStore',
  'recurringOrderStore'
)(ProductAddToCart);



// WEBPACK FOOTER //
// ./src/components/product/ProductAddToCart/index.jsx